import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import CustomModal from "../../common/CustomModal";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import AdsAPIs from "../../../apiRoutes/Ads";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { GET_AD_REQUEST_QUERY_KEY, GET_AD_REQUESTS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { formatDisplayDateTime } from "../../../utils/helperFunctions";
import { IssueChips } from "../../AdModeration/ModerationIssues";

const REASON_MIN = 20;
const REASON_MAX = 4096;

/** Requester approves a hook rejected only for Editing issues (super admin: any); the moderator's call is shown read-only above. */
const OverrideModerationModal = ({ open, onClose, moderation, hookLabel }) => {
  const [reason, setReason] = useState("");
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      AdsAPIs.overrideModeration({
        axiosPrivate,
        id: moderation.id,
        payload: { reason: reason.trim() },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUEST_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success("Rejection overridden. The hook is now approved.");
      onClose();
    },
  });
  const short = reason.trim().length < REASON_MIN;
  const notes = Array.isArray(moderation?.notes) ? moderation.notes : [];
  return (
    <CustomModal open={open} title={`Override rejection · ${hookLabel}`} onClose={onClose} isButtonsRequired={false} className="lg:!w-[560px]">
      <div className="space-y-4 py-2">
        <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/40 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-500">Moderator rejected</p>
          <IssueChips codes={moderation?.issues_highlighted} />
          {notes.length > 0 && <p className="whitespace-pre-wrap text-sm text-gray-700">{notes.join("\n")}</p>}
          <p className="text-xs text-gray-400">
            {moderation?.last_moderated_by_name || "Moderator"}
            {moderation?.updated_on ? ` · ${formatDisplayDateTime(moderation.updated_on)}` : ""}
            {moderation?.finalized_at ? ` · finalized ${formatDisplayDateTime(moderation.finalized_at)}` : ""}
          </p>
        </div>
        {!moderation?.overridable && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This rejection includes non-Editing issues. You can override it only as a Super Admin.
          </p>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="override-reason">
            Override reason <span className="text-red-500">*</span>
          </Label>
          <textarea
            id="override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX}
            rows={3}
            placeholder="Why is this fine to launch as it is?"
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <p className={`text-xs ${short ? "text-gray-400" : "text-emerald-600"}`}>
            {reason.trim().length} / {REASON_MIN} characters minimum
          </p>
        </div>
        <p className="text-xs text-muted-foreground">The hook becomes final approved straight away, with no moderator re-review.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutate()} disabled={short || isPending}>
            {isPending ? "Overriding…" : "Override rejection"}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default OverrideModerationModal;
